import React, { useEffect } from 'react';
import { X, CheckCircle, AlertCircle, AlertTriangle, Info, HelpCircle } from 'lucide-react';

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  type = 'confirm',
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  showCancel = true
}) => {
  // Fechar com ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Configurações por tipo
  const typeConfig = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-400',
      bgColor: 'bg-green-500/10',
      borderColor: 'border-green-500/30',
      buttonColor: 'bg-green-500 hover:bg-green-600'
    },
    error: {
      icon: AlertCircle,
      iconColor: 'text-red-400',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
      buttonColor: 'bg-red-500 hover:bg-red-600'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-yellow-400',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
      buttonColor: 'bg-yellow-500 hover:bg-yellow-600'
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-400',
      bgColor: 'bg-blue-500/10',
      borderColor: 'border-blue-500/30',
      buttonColor: 'bg-blue-500 hover:bg-blue-600'
    },
    confirm: {
      icon: HelpCircle,
      iconColor: 'text-purple-400',
      bgColor: 'bg-purple-500/10',
      borderColor: 'border-purple-500/30',
      buttonColor: 'bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700'
    }
  };

  const config = typeConfig[type] || typeConfig.confirm;
  const Icon = config.icon;

  // Se for info, success ou error sem onConfirm, não mostrar botão de confirmar
  const showConfirmButton = (type === 'confirm' || type === 'warning') && onConfirm;
  const actualShowCancel = showCancel && (showConfirmButton || type === 'info' || type === 'success' || type === 'error');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div 
        className={`
          relative bg-slate-900 border-2 ${config.borderColor} rounded-lg 
          shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200
        `}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-4 mb-4">
          <div className={`${config.bgColor} p-3 rounded-full`}>
            <Icon className={`w-6 h-6 ${config.iconColor}`} />
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-black text-white mb-2">{title}</h2>
            <p className="text-gray-300 text-sm leading-relaxed">{message}</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors p-1 hover:bg-white/10 rounded"
          >
            <X size={20} />
          </button>
        </div>

        {/* Actions */}
        {(showConfirmButton || actualShowCancel) && (
          <div className="flex gap-3 mt-6">
            {actualShowCancel && (
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2.5 bg-white/5 hover:bg-white/10 border border-white/10 rounded-md font-bold text-sm transition-colors"
              >
                {cancelText}
              </button>
            )}
            {showConfirmButton && (
              <button
                onClick={() => {
                  if (onConfirm) onConfirm();
                  onClose();
                }}
                className={`flex-1 px-4 py-2.5 ${config.buttonColor} rounded-md font-bold text-sm text-white transition-all shadow-lg hover:shadow-xl`}
              >
                {confirmText}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConfirmModal;

